import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import ConfirmModal from 'Components/ConfirmModal';
import { createPitchToZoom } from 'Components/Interact/common/pitchToZoom';
import {
  calculatePreviewScale,
  sortFieldPredicate,
  updateScrollOnScale,
} from 'Components/Interact/common/utils';
import { FieldItem } from 'Components/Interact/components/FieldItem';
import ScaleDropDown from 'Components/Interact/components/ScaleDropDown';
import { PDFDocument } from 'Components/PDFDocument';
import UIButton from 'Components/UIComponents/UIButton';
import UISpinner from 'Components/UIComponents/UISpinner';
import { useIsTablet, useModal } from 'Hooks/Common';
import { useDocumentFieldUpdateLocally } from 'Hooks/DocumentFields';
import { useDocumentSignInit, useSubmitSignedDocument } from 'Hooks/DocumentSign';
import { PDFMetadata } from 'Interfaces/Common';
import { Document, InteractModes } from 'Interfaces/Document';
import { DocumentField, DocumentFieldTypes } from 'Interfaces/DocumentFields';
import { SelectedSignature } from 'Interfaces/Requisite';
import { throttle } from 'lodash-es';
import Toast from 'Services/Toast';
import { getCurrentDate } from 'Utils/functions';
import { selectDocumentFields } from 'Utils/selectors';

import { DocumentPreviewPage } from '../../../Components/DocumentPage';

import { SigningButtonMobile } from './SigningButtonMobile';
import TermsModal from './TermsModal';

interface DocumentSignViewProps {
  document: Document;
  redirectionPage: string;
  openSuccessModal: () => void;
}

const DocumentSignView = ({
  document,
  redirectionPage,
  openSuccessModal,
}: DocumentSignViewProps) => {
  const pdfPageMeta = document.pdfMetadata as PDFMetadata;

  const mainRef = useRef<HTMLDivElement>(null);
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const [pagesContainer, setPagesContainer] = useState<HTMLDivElement | null>(null);

  const greaterPageWidth = useMemo<number>(() => {
    return Object.values(pdfPageMeta).reduce((greaterWidth, pageMetadata) => {
      return greaterWidth < pageMetadata.width ? pageMetadata.width : greaterWidth;
    }, 0);
  }, [pdfPageMeta]);

  const isTablet = useIsTablet();

  const [, setPages] = useState<number[]>([]);
  const [pageCount, setPageCount] = useState<number>(0);
  const [isAutoUpdated, setIsAutoUpdated] = useState(false);
  const [pagesIsLoaded, setPagesIsLoaded] = useState(false);
  const [currentFieldId, setCurrentFieldId] = useState<DocumentField['id']>();
  const [lastFocusedFieldId, setLastFocusedFieldId] = useState<DocumentField['id']>();
  const [nextStepButtonTitle, setNextStepButtonTitle] = useState('Start signing');
  const [scaleCoefficient, setScaleCoefficient] = useState(1);
  const [baseScale, setBaseScale] = useState(() => {
    return calculatePreviewScale(greaterPageWidth, mainRef.current) || scaleCoefficient;
  });

  const documentFields: DocumentField[] = useSelector(selectDocumentFields);

  const [submitDocument, isSubmitLoading] = useSubmitSignedDocument();
  const handleDocumentFieldUpdate = useDocumentFieldUpdateLocally();
  const [initDocument, isInitializingDocument] = useDocumentSignInit(document);
  const [pdfUrl, setPdfUrl] = useState<string | undefined>();

  const [
    lastSelectedSignature,
    setLastSelectedSignature,
  ] = useState<SelectedSignature | null>(null);

  const pageStyle = useMemo(() => {
    const pdfMeta = document?.pdfMetadata && document.pdfMetadata[1];

    return {
      marginBottom: 20,
      width: pdfMeta?.width,
      height: pdfMeta?.height,
    };
  }, [document]);

  const documentScale = baseScale * scaleCoefficient;

  const handleScaleCoefficientChange = useCallback(
    (nextDocumentScale: number, anchor?: { clientX: number; clientY: number }) => {
      updateScrollOnScale({
        element: pagesContainer,
        anchor,
        documentScale,
        nextDocumentScale,
      });

      setScaleCoefficient(nextDocumentScale);
    },
    [pagesContainer, documentScale],
  );

  useEffect(() => {
    if (!pagesContainer) return;

    const pitchToZoom = createPitchToZoom(pagesContainer, {
      scale: scaleCoefficient,
      onTouchMoveComplete: handleScaleCoefficientChange,
    });
    pitchToZoom.attachListeners();
    return () => void pitchToZoom.detachListeners();
  }, [pagesContainer, scaleCoefficient, handleScaleCoefficientChange]);

  const updateContainerBaseRatio = useCallback(
    (container: HTMLElement | null) => {
      const baseRatio = calculatePreviewScale(greaterPageWidth, container);
      if (typeof baseRatio !== 'undefined' && baseRatio > 0) {
        setBaseScale(baseRatio);
      }
    },
    [greaterPageWidth],
  );

  useEffect(() => {
    const mainContainer = mainRef.current;

    if (!mainContainer) return;

    function resize() {
      updateContainerBaseRatio(mainContainer);
    }

    resize();
    const throttledResize = throttle(resize, 750);

    window.addEventListener('resize', throttledResize);
    return () => window.removeEventListener('resize', throttledResize);
  }, [updateContainerBaseRatio]);

  const [requiredUnsignedFields, requiredUnsignedFieldsCount] = useMemo(() => {
    const fields = documentFields
      .filter(field => !field.signed && field.required)
      .sort(sortFieldPredicate);

    return [fields, fields.length];
  }, [documentFields]);

  const calculateScrollPageAlign = useCallback(
    (requiredUnsignedField: DocumentField) => {
      const pageNumber = requiredUnsignedField.pageNumber;

      const x = pdfPageMeta[pageNumber].height / requiredUnsignedField.coordinateY;

      if (x >= 1 && x <= 2) return 'end';
      if (x > 2 && x <= 3) return 'center';
      if (x > 3) return 'start';

      return 'center';
    },
    [pdfPageMeta],
  );

  const handleFocusNextField = useCallback(() => {
    setNextStepButtonTitle('Next step');

    if (requiredUnsignedFields.length === 0) {
      return;
    }

    if (!currentFieldId) {
      return setCurrentFieldId(requiredUnsignedFields[0].id);
    }

    const currentIndex = requiredUnsignedFields
      .map(field => field.id)
      .indexOf(currentFieldId);

    if (requiredUnsignedFields.length === 1 && currentIndex === 0) {
      virtuosoRef.current?.scrollToIndex({
        index: requiredUnsignedFields[0].pageNumber - 1,
        align: calculateScrollPageAlign(requiredUnsignedFields[0]),
      });
      setTimeout(() => setCurrentFieldId(requiredUnsignedFields[0].id), 0);
      return setCurrentFieldId(undefined);
    }

    if (requiredUnsignedFields.length - 1 === currentIndex) {
      virtuosoRef.current?.scrollToIndex({
        index: requiredUnsignedFields[0].pageNumber - 1,
        align: calculateScrollPageAlign(requiredUnsignedFields[0]),
      });
      return setCurrentFieldId(requiredUnsignedFields[0].id);
    }

    virtuosoRef.current?.scrollToIndex({
      index: requiredUnsignedFields[currentIndex + 1].pageNumber - 1,
      align: calculateScrollPageAlign(requiredUnsignedFields[currentIndex + 1]),
    });

    setCurrentFieldId(requiredUnsignedFields[currentIndex + 1].id);
  }, [calculateScrollPageAlign, currentFieldId, requiredUnsignedFields]);

  const handleTabKeydown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        event.preventDefault();
        return handleFocusNextField();
      }
    },
    [handleFocusNextField],
  );

  const handleSubmitDocument = useCallback(() => {
    submitDocument({
      documentId: document.id,
      fields: documentFields,
      signerId: document.signers[0].id,
    })
      .then(() => {
        Toast.success('Document submitted.');
        if (redirectionPage) {
          window.location.href = redirectionPage;
        } else {
          openSuccessModal();
        }
      })
      .catch(error => {
        Toast.handleErrors(error);
      });
  }, [
    document.id,
    document.signers,
    documentFields,
    openSuccessModal,
    redirectionPage,
    submitDocument,
  ]);

  const [showTermsModal, hideTermsModal] = useModal(
    () => (
      <TermsModal
        documentName={document.title}
        onClose={hideTermsModal}
        onCancel={hideTermsModal}
        onConfirm={handleSubmitDocument}
        isConfirmLoading={isSubmitLoading}
      />
    ),
    [handleSubmitDocument],
  );

  const [showTestModeModal, hideTestModeModal] = useModal(
    () => (
      <ConfirmModal
        onClose={hideTestModeModal}
        onConfirm={hideTestModeModal}
        isCancellable={false}
      >
        <div className="billing__plan-modal">
          <div className="billing__plan-modal-title">
            Document was created in test mode
          </div>
          <div className="billing__plan-modal-subtitle">
            This document is not legally binding.
          </div>
        </div>
      </ConfirmModal>
    ),
    [handleSubmitDocument],
  );

  const handleDocumentInit = useCallback(() => {
    initDocument(undefined)
      .then(({ 0: documentPdf }) => {
        setPdfUrl(documentPdf.result);
      })
      .catch(err => {
        Toast.handleErrors(err);
      });
  }, [initDocument]);

  useEffect(() => {
    handleDocumentInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (documentFields.length === 0 || isAutoUpdated) return;
    documentFields.forEach(field => {
      switch (field.type) {
        case DocumentFieldTypes.Date:
          handleDocumentFieldUpdate({
            id: field.id,
            text: getCurrentDate(
              field.dateFormat as NonNullable<DocumentField['dateFormat']>,
            ),
          });
          break;
        case DocumentFieldTypes.Name: {
          const signer = document.signers.find(signer => signer?.id === field.signerId);
          handleDocumentFieldUpdate({ id: field.id, text: signer?.name });
          break;
        }
      }
    });
    setIsAutoUpdated(true);
  }, [document.signers, documentFields, handleDocumentFieldUpdate, isAutoUpdated]);

  useEffect(() => {
    if (document.testMode) showTestModeModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePdfLoadSuccess = useCallback(pdfDocumentProxy => {
    setPages(Array.from({ length: pdfDocumentProxy.numPages }, (_, i) => i + 1));
    setPagesIsLoaded(true);
    setPageCount(pdfDocumentProxy.numPages);
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleTabKeydown);

    return () => window.removeEventListener('keydown', handleTabKeydown);
  }, [handleTabKeydown]);

  if (isInitializingDocument) {
    return (
      <div className="document-sign__main">
        <UISpinner wrapperClassName="spinner--main__wrapper" width={50} height={50} />
      </div>
    );
  }

  return (
    <div className="document-sign__main" ref={mainRef}>
      <div className="document-sign__content-wrapper">
        <div className="document-sign__content-header">
          <div className="document-sign__content-header-item">
            <span className="document-sign__dots" />
            {requiredUnsignedFieldsCount > 0 && (
              <div className="document-sign__content-header-counter">
                FIELDS LEFT:
                <div className="document-sign__content-header-count-wrapper">
                  <span className="document-sign__content-header-count">
                    {requiredUnsignedFieldsCount}
                  </span>
                </div>
              </div>
            )}
          </div>
          <div className="document-sign__content-header-scale">
            <ScaleDropDown
              changeScale={setScaleCoefficient}
              documentScale={scaleCoefficient}
            />
          </div>
          <div className="document-sign__content-header-item">
            {requiredUnsignedFieldsCount > 0 ? (
              <UIButton
                priority="primary"
                title={nextStepButtonTitle}
                handleClick={handleFocusNextField}
                disabled={!pagesIsLoaded}
              />
            ) : (
              <UIButton
                priority="primary"
                handleClick={showTermsModal}
                title="Submit"
                isLoading={isSubmitLoading}
                disabled={isSubmitLoading}
              />
            )}
          </div>
        </div>
        <div className="document-sign__content" ref={setPagesContainer}>
          <div
            className="document-sign__pages-container"
            style={{ width: greaterPageWidth * documentScale }}
          >
            {pdfUrl && (
              <PDFDocument
                file={pdfUrl}
                onLoadSuccess={handlePdfLoadSuccess}
                externalLinkTarget="_blank"
              >
                <Virtuoso
                  key={scaleCoefficient}
                  ref={virtuosoRef}
                  style={{
                    height: window.innerHeight - (isTablet ? 175 : 245),
                    //FIXME: 10 is a magic number (not in document preview component)
                    width: greaterPageWidth * (baseScale || 1) + 10,
                  }}
                  totalCount={pageCount}
                  increaseViewportBy={500}
                  itemContent={index => {
                    const pageNumber = index + 1;
                    const pageMeta = pdfPageMeta && pdfPageMeta[pageNumber];
                    const pageFields = documentFields.filter(
                      field => field.pageNumber === pageNumber,
                    );

                    return (
                      <DocumentPreviewPage
                        pageNumber={pageNumber}
                        scale={documentScale}
                        style={{
                          ...pageStyle,
                          height: pageMeta.height,
                          width: pageMeta.width,
                        }}
                      >
                        {pageFields.map(field => (
                          <FieldItem
                            key={field.id}
                            field={field}
                            inFocus={currentFieldId === field.id}
                            documentScale={baseScale}
                            selectedScale={scaleCoefficient}
                            onFocus={setCurrentFieldId}
                            isResizable={false}
                            onChangeSigner={() => {}}
                            interactMode={InteractModes.SIGNING}
                            onDocumentFieldUpdate={handleDocumentFieldUpdate}
                            lastSelectedSignature={lastSelectedSignature}
                            setLastSelectedSignature={setLastSelectedSignature}
                            lastFocusedFieldId={lastFocusedFieldId}
                            setLastFocusedFieldId={setLastFocusedFieldId}
                          />
                        ))}
                      </DocumentPreviewPage>
                    );
                  }}
                />
              </PDFDocument>
            )}
          </div>
        </div>
      </div>
      {requiredUnsignedFieldsCount > 0 ? (
        <SigningButtonMobile
          title={nextStepButtonTitle}
          handleClick={handleFocusNextField}
          disabled={!pagesIsLoaded}
        />
      ) : (
        <SigningButtonMobile
          handleClick={showTermsModal}
          title="Submit"
          isLoading={isSubmitLoading}
          disabled={isSubmitLoading}
        />
      )}
    </div>
  );
};

export default DocumentSignView;
